import { useCallback, useRef } from 'react';
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const STORAGE_KEY = 'chag_brewmaster_session';
const TTL_MS = 30 * 60 * 1000; // 30 minutes (sessionStorage)
const DEBOUNCE_MS = 500;
const FIRESTORE_DEBOUNCE_MS = 3000;
const MIN_MESSAGES_FOR_FIRESTORE = 3;
const FIRESTORE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Persists Brewmaster conversation state with two tiers:
 *
 * Tier 1: sessionStorage (fast, synchronous, handles in-app navigation)
 * Tier 2: Firestore (durable, async, handles browser tab switches and reloads)
 *
 * loadSession (sync): checks sessionStorage only. Used for initial hydration.
 * loadFromFirestore (async): checks Firestore. Called as a fallback in useEffect.
 * saveSession: writes to both tiers (debounced).
 */
export default function useBrewmasterSession(userId) {
  const sessionTimerRef = useRef(null);
  const firestoreTimerRef = useRef(null);

  const getFirestoreRef = useCallback(() => {
    if (!userId) return null;
    return doc(db, 'users', userId, 'brewmasterConversations', 'active');
  }, [userId]);

  /**
   * Synchronous load from sessionStorage. Used for instant hydration on mount.
   */
  const loadSession = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const session = JSON.parse(raw);
      if (!session.timestamp || Date.now() - session.timestamp > TTL_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return {
        messages: session.messages,
        proposal: session.proposal || null,
        parsedCharacter: session.parsedCharacter || null,
        restoredAt: session.timestamp,
      };
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }, []);

  /**
   * Async fallback: loads from Firestore when sessionStorage is empty.
   * Called in a useEffect after mount.
   */
  const loadFromFirestore = useCallback(async () => {
    const ref = getFirestoreRef();
    if (!ref) return null;

    try {
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;

      const session = snap.data();
      if (!session.timestamp || Date.now() - session.timestamp > FIRESTORE_TTL_MS) {
        await deleteDoc(ref);
        return null;
      }

      // Hydrate sessionStorage for fast subsequent loads
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      } catch {
        // sessionStorage full
      }

      return {
        messages: session.messages,
        proposal: session.proposal || null,
        parsedCharacter: session.parsedCharacter || null,
        restoredAt: session.timestamp,
      };
    } catch {
      return null;
    }
  }, [getFirestoreRef]);

  const saveSession = useCallback((state) => {
    const payload = {
      messages: state.messages,
      proposal: state.proposal,
      parsedCharacter: state.parsedCharacter,
      timestamp: Date.now(),
    };

    // Tier 1: sessionStorage (debounced 500ms)
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(() => {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch {
        // sessionStorage full or unavailable
      }
    }, DEBOUNCE_MS);

    // Tier 2: Firestore (debounced 3s, only for meaningful conversations)
    const ref = getFirestoreRef();
    if (!ref) return;

    const messageCount = state.messages?.length || 0;
    if (messageCount < MIN_MESSAGES_FOR_FIRESTORE) return;

    if (firestoreTimerRef.current) clearTimeout(firestoreTimerRef.current);
    firestoreTimerRef.current = setTimeout(async () => {
      try {
        await setDoc(ref, payload);
      } catch {
        // Firestore write failed
      }
    }, FIRESTORE_DEBOUNCE_MS);
  }, [getFirestoreRef]);

  const clearSession = useCallback(async () => {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    if (firestoreTimerRef.current) clearTimeout(firestoreTimerRef.current);
    sessionStorage.removeItem(STORAGE_KEY);

    const ref = getFirestoreRef();
    if (ref) {
      try {
        await deleteDoc(ref);
      } catch {
        // Best effort cleanup
      }
    }
  }, [getFirestoreRef]);

  const cleanup = useCallback(() => {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    if (firestoreTimerRef.current) clearTimeout(firestoreTimerRef.current);
  }, []);

  return { loadSession, loadFromFirestore, saveSession, clearSession, cleanup };
}
